import moment, { Moment } from 'moment';
import { EndOfWeekDay } from '@bringg/types';
import { dateUtils } from '@bringg-frontend/utils';
import type { CreateSpeedFactor } from '@bringg/dashboard-sdk/dist/SpeedFactor/Service/SpeedFactor.service';

import { ISO_WEEK_END_DAY_NUMBER, ISO_WEEK_START_DAY_NUMBER } from '../../constants';
import { trafficVariablesConfig } from '../traffic-variables-config';
import type { RangeProcessingData } from './types';

export const serverWeekDayToMomentWeekDay = (weekday: number) => weekday - 1;

export const momentWeekDayToServerWeekDay = (weekday: number) => weekday + 1;

// ISO weekdays: 1 - Monday, 7 - Sunday
// End Of Weekday: Friday is 0, Saturday is 1, through to 6 for Thursday
export const isoWeekdayToEndOfWeekday = (weekday: number) => {
	const map = { 1: 3, 2: 4, 3: 5, 4: 6, 5: 0, 6: 1, 7: 2 };

	return map[weekday];
};

// SpeedFactor weekdays: 1 - 7 meaning Sunday - Saturday
// End Of Weekday: Friday is 0, Saturday is 1, through to 6 for Thursday
const endOfWeekdayToSpeedfactorWeekday = (weekday: EndOfWeekDay) => {
	const map = { 0: 6, 1: 7, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5 };

	return map[weekday];
};

export const daysFromEndOfWeekDayToIsoWeekday = (dates: EndOfWeekDay[]) => {
	return dates.map(date => endOfWeekdayToSpeedfactorWeekday(date));
};

type CreateProps = {
	type: 'create';
	start: Moment;
	timezone: string;
};

type EditProps = {
	type: 'edit';
	prevStart: Moment;
	nextStart: Moment;
};

type GetWeekDayShiftProps = CreateProps | EditProps;

export const getBaseWeekDayShiftInUTC = (props: GetWeekDayShiftProps): number => {
	if (props.type === 'create') {
		const timezoneUtcOffset = moment().tz(props.timezone).utcOffset();
		const [utcDay, timezoneDay] = [props.start.clone().utc().day(), props.start.clone().tz(props.timezone).day()];

		// Not using moment.isSame here due to the fact we have only one date here + isSame timezone coercion
		if (utcDay === timezoneDay) return 0;

		return timezoneUtcOffset > 0 ? -1 : 1;
	} else {
		const [prevUtcStart, nextUtcStart] = [moment(props.prevStart).utc(), moment(props.nextStart).utc()];

		if (prevUtcStart.isSame(nextUtcStart, 'day')) return 0;

		// Hours diff as it is the smallest granularity our calendar allows
		const hoursDiff = nextUtcStart.diff(prevUtcStart, 'hours');
		// If changing this
		// Pay attention how reminder and subsequent subtraction works with positive / negative values
		const hoursRemainder = hoursDiff % 24;
		const fullDaysShift = hoursDiff === hoursRemainder ? 0 : (hoursDiff - hoursRemainder) / 24;

		if (prevUtcStart.isSame(prevUtcStart.clone().add(hoursRemainder, 'hours'), 'day')) return fullDaysShift;

		if (fullDaysShift === 0) {
			return nextUtcStart > prevUtcStart ? 1 : -1;
		}

		return fullDaysShift > 0 ? fullDaysShift + 1 : fullDaysShift - 1;
	}
};

type GetShiftedWeekDayGenericProps = {
	weekDayNumber: number;
	daysShift: number;
	weekStartDayNumber: number;
	weekEndDayNumber: number;
};

export const getShiftedWeekDayGeneric = ({
	weekStartDayNumber,
	weekEndDayNumber,
	weekDayNumber,
	daysShift
}: GetShiftedWeekDayGenericProps) => {
	const normalizedShift = daysShift % 7;

	if (normalizedShift === 0) return weekDayNumber;

	let normalizedDay = weekDayNumber;
	if (weekDayNumber < weekStartDayNumber) {
		normalizedDay = weekStartDayNumber;
	} else if (weekDayNumber > weekEndDayNumber) {
		normalizedDay = weekEndDayNumber;
	}

	const shiftedWeekDay = normalizedDay + normalizedShift;
	if (shiftedWeekDay > weekEndDayNumber) {
		return shiftedWeekDay - 7;
	} else if (shiftedWeekDay < weekStartDayNumber) {
		return shiftedWeekDay + 7;
	}
	return shiftedWeekDay;
};

/**
 * Must be used only when working with ISO week days [1,7]
 * @param weekDays - array of day_of_week numbers
 * @param daysShift - days by which each element from array is shifted
 */
export const getCorrectedISOWeekDays = (
	weekDays: number[],
	// Can calculate it inside from GetWeekDayShiftProps when outer dependencies on daysShift is removed
	daysShift: number
) => {
	if (!trafficVariablesConfig.enableWeekDayCorrection || !daysShift) return weekDays;

	return getShiftedISOWeekDays(weekDays, daysShift);
};

/**
 * Must be used only when working with ISO week days [1,7]
 * @param weekDays - array of day_of_week numbers;
 * @param daysShift - days by which each element from array is shifted
 */
export const getShiftedISOWeekDays = (weekDays: number[], daysShift: number) =>
	daysShift
		? weekDays.map(weekDayNumber =>
				getShiftedWeekDayGeneric({
					weekStartDayNumber: ISO_WEEK_START_DAY_NUMBER,
					weekEndDayNumber: ISO_WEEK_END_DAY_NUMBER,
					weekDayNumber,
					daysShift
				})
		  )
		: weekDays;

export const removeInvalidWeekDays = ({
	processingData,
	speedFactorConfig
}: {
	processingData: RangeProcessingData;
	speedFactorConfig: CreateSpeedFactor;
}): CreateSpeedFactor => {
	const { effectiveEndDate, effectiveStartDate } = processingData;
	const { days_of_week, start_date_day, end_date_day, start_date_month, end_date_month } = speedFactorConfig;

	speedFactorConfig.days_of_week = dateUtils
		.getMomentWeekdaysMatchingDateRange({
			weekDays: days_of_week.map(serverWeekDayToMomentWeekDay),
			rangeStart: moment
				.utc()
				.year(effectiveStartDate.year())
				.month(start_date_month - 1)
				.date(start_date_day),
			rangeEnd: moment
				.utc()
				.year(effectiveEndDate.year())
				.month(end_date_month - 1)
				.date(end_date_day)
		})
		.map(momentWeekDayToServerWeekDay);

	return speedFactorConfig;
};
